import type { PanelProps } from "../panel-props"

import { resetState } from "../../../have-you-seen-it/poem-batch-controller"
import { useToggleSettings } from "../../../hooks/use-toggle-settings"
import { IS_AUTH } from "../../../modules/auth"
import { useSettings } from "../../../modules/settings"
import { Select } from "../../select"
import { Slider } from "../../slider"
import { ToggleButton } from "../../toggle-button"
import { SharedSettings } from "../shared-settings.tsx"
import { useToggleHaveYouSeenItSetting } from "../toggle-app-setting-hooks.ts"

const SPEED = {
  max: 2.5,
  min: 0.3,
  step: 0.1,
}

const SOURCE_OPTIONS = [
  { key: "none", label: "—" },
  { key: "de", label: "German" },
  { key: "poems", label: "Poems" },
  { key: "poems-batch", label: "Poems (batched)" },
  { key: "random-sentence", label: "Random Sentences" },
  { key: "random-words", label: "Random Words" },
  { key: "pair-of-words", label: "Pair of Words" },
]

export function HaveYouSeenItSettings(
  input: PanelProps & { children?: React.ReactNode },
) {
  const {
    appSettings,
    appSettings: {
      shared: { privateData },
    },
    setAppSettings,
  } = useSettings()
  const { isOn: privateDataIsOn, toggle: togglePrivateData } =
    useToggleSettings({
      currentValue: privateData,
      setValue: newValue=> {
        setAppSettings("privateData", newValue, "shared")
      },
    })
  const [smallerTextIsOn, toggleSmallerText] =
    useToggleHaveYouSeenItSetting("smallerText")
  const [intervalIsFullySeenIsOn, toggleIntervalIsFullySeen] =
    useToggleHaveYouSeenItSetting("intervalIsFullySeen")
  const [hideErrorNotificationIsOn, toggleHideErrorNotification] =
    useToggleHaveYouSeenItSetting("hideErrorNotification")
  const [reviewModeIsOn, toggleReviewMode] =
    useToggleHaveYouSeenItSetting("reviewMode")

  const onSpeedValueChange = (newSpeed: number)=> {
    setAppSettings("speed", newSpeed, "haveYouSeenIt")
  }

  const onChangeSource = (value: string)=> {
    setAppSettings("source", value, "haveYouSeenIt")
  }

  const onChangeNumberOfRepeats = (value: number)=> {
    setAppSettings("poemsNumberOfRepeats", value, "haveYouSeenIt")
    resetState()
  }

  return (
    <SharedSettings closeSidebar={input.closeSidebar}>
      {IS_AUTH && (
        <>
          <section className="sidebar__section">
            <h3 className="sidebar__section-title">Source</h3>
            <Select
              initialValue={appSettings.haveYouSeenIt.source}
              label="Source"
              onChange={onChangeSource}
              options={SOURCE_OPTIONS}
            />
          </section>

          <div className="sidebar__separator" role="separator" />
        </>
      )}

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Display</h3>
        <ToggleButton
          checked={smallerTextIsOn}
          label="Use smaller text"
          onChange={toggleSmallerText}
        />
        <ToggleButton
          checked={hideErrorNotificationIsOn}
          label="Supress error notification"
          onChange={toggleHideErrorNotification}
        />
      </section>

      <div className="sidebar__separator" role="separator" />

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Playback</h3>
        <ToggleButton
          checked={intervalIsFullySeenIsOn}
          label="Interval is fully seen"
          onChange={toggleIntervalIsFullySeen}
        />
        <ToggleButton
          checked={reviewModeIsOn}
          label="Review mode"
          onChange={toggleReviewMode}
        />
        <Slider
          label="Speed"
          max={SPEED.max}
          min={SPEED.min}
          onValueChange={onSpeedValueChange}
          step={SPEED.step}
          value={appSettings.haveYouSeenIt.speed}
        />
      </section>

      {appSettings.haveYouSeenIt.source === "poems-batch" && (
        <>
          <div className="sidebar__separator" role="separator" />
          <section className="sidebar__section">
            <h3 className="sidebar__section-title">Batch</h3>
            <Slider
              label="Number of repeats in each batch"
              max={3}
              min={1}
              onValueChange={onChangeNumberOfRepeats}
              step={1}
              value={appSettings.haveYouSeenIt.poemsNumberOfRepeats}
            />
          </section>
        </>
      )}

      {appSettings.haveYouSeenIt.source === "random-sentence" && IS_AUTH && (
        <>
          <div className="sidebar__separator" role="separator" />
          <section className="sidebar__section">
            <h3 className="sidebar__section-title">Privacy</h3>
            <ToggleButton
              checked={privateDataIsOn}
              label="Private Data"
              onChange={togglePrivateData}
            />
          </section>
        </>
      )}

      {input.children}
    </SharedSettings>
  )
}
